import React, { useState, useEffect, useRef } from 'react';
import { PlayCircle, ArrowRight, CheckCircle } from 'lucide-react';
import { mockData } from '../mock';

const Methodology = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Auto-advance steps
  useEffect(() => {
    if (isVisible) {
      const interval = setInterval(() => {
        setActiveStep((prev) => (prev + 1) % mockData.methodology.length);
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [isVisible]);

  return (
    <section ref={sectionRef} className="py-24 bg-gradient-to-b from-black to-gray-900 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full cyber-grid opacity-5" />
        <div className="absolute top-1/3 right-0 w-96 h-96 bg-gradient-to-l from-cyan-500/10 to-transparent rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-16 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-400/30 rounded-full glass-effect mb-6">
            <PlayCircle className="w-4 h-4 text-cyan-400" />
            <span className="text-sm font-medium text-cyan-400">Nossa Metodologia</span>
          </div>
          
          <h2 className="heading-1 mb-6">
            <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              Processo Estruturado de Pentest
            </span>
          </h2>
          
          <p className="body-large text-gray-300 max-w-3xl mx-auto">
            Utilizamos uma metodologia estruturada baseada em melhores práticas internacionais
            para garantir testes abrangentes e resultados confiáveis.
          </p>
        </div>

        {/* Methodology Steps */}
        <div className="relative">
          {/* Progress Line */}
          <div className="absolute top-8 left-8 right-8 h-0.5 bg-gray-700 hidden lg:block">
            <div 
              className="h-full bg-gradient-to-r from-cyan-400 to-blue-500 transition-all duration-1000"
              style={{ width: `${((activeStep + 1) / mockData.methodology.length) * 100}%` }}
            />
          </div>

          {/* Steps Grid */}
          <div className="grid lg:grid-cols-3 gap-8">
            {mockData.methodology.map((step, index) => (
              <div
                key={index}
                className={`relative group cursor-pointer ${
                  isVisible ? 'animate-fadeInUp' : 'opacity-0'
                } ${index === activeStep ? 'scale-105' : ''}`}
                style={{ animationDelay: `${index * 0.2}s` }}
                onClick={() => setActiveStep(index)}
              >
                {/* Step Card */}
                <div className={`p-6 glass-effect border rounded-lg transition-all duration-500 ${
                  index === activeStep 
                    ? 'border-cyan-400 bg-cyan-400/5' 
                    : 'border-white/10 hover:border-cyan-400/50'
                }`}>
                  {/* Step Number */}
                  <div className="relative mb-4">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-500 ${
                      index === activeStep
                        ? 'bg-gradient-to-r from-cyan-400 to-blue-500 text-black'
                        : 'glass-effect border border-cyan-400/30 text-cyan-400'
                    }`}>
                      <span className="text-xl font-bold">{step.step}</span>
                    </div>
                    
                    {/* Active Indicator */}
                    {index === activeStep && (
                      <div className="absolute inset-0 w-16 h-16 rounded-full bg-cyan-400/20 animate-ping" />
                    )}
                  </div>

                  {/* Content */}
                  <h3 className={`heading-3 mb-3 transition-colors duration-300 ${
                    index === activeStep ? 'text-cyan-400' : 'text-white group-hover:text-cyan-400'
                  }`}>
                    {step.title}
                  </h3>
                  
                  <p className="body-small text-gray-300 leading-relaxed">
                    {step.description}
                  </p>

                  {/* Active Step Indicator */}
                  {index === activeStep && (
                    <div className="flex items-center mt-4 text-cyan-400">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      <span className="text-sm font-medium">Etapa Ativa</span>
                    </div>
                  )}
                </div>

                {/* Connection Arrow (Desktop) */}
                {index < mockData.methodology.length - 1 && (
                  <div className="hidden lg:block absolute top-8 -right-4 z-10">
                    <ArrowRight className={`w-8 h-8 transition-colors duration-500 ${
                      index <= activeStep ? 'text-cyan-400' : 'text-gray-600'
                    }`} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step Details */}
        <div className={`mt-16 glass-effect border border-cyan-400/30 rounded-lg p-8 ${
          isVisible ? 'animate-fadeInUp' : 'opacity-0'
        }`} style={{ animationDelay: '1.2s' }}>
          <div className="text-center">
            <h3 className="heading-2 text-white mb-4">
              Etapa {mockData.methodology[activeStep].step}: {mockData.methodology[activeStep].title}
            </h3>
            
            <p className="body-large text-gray-300 max-w-3xl mx-auto leading-relaxed">
              {mockData.methodology[activeStep].description}
            </p>
            
            {/* Progress Indicators */}
            <div className="flex justify-center space-x-2 mt-8">
              {mockData.methodology.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveStep(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === activeStep 
                      ? 'bg-cyan-400 scale-125' 
                      : 'bg-gray-600 hover:bg-gray-500'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className={`text-center mt-16 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{ animationDelay: '1.4s' }}>
          <h3 className="heading-3 text-white mb-4">
            Pronto para começar seu <span className="text-cyan-400">Pentest</span>?
          </h3>
          <p className="body-medium text-gray-300 mb-8 max-w-2xl mx-auto">
            Nossa metodologia garante uma avaliação completa e profissional da sua infraestrutura de segurança.
          </p>
          
          <button 
            onClick={() => {
              const contactSection = document.querySelector('#contact');
              if (contactSection) {
                contactSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="btn-primary group"
          >
            <span>Solicitar Orçamento</span>
            <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Methodology;